import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Search } from "lucide-react"
import { fieldInputClass, focusRing, formButtonClass, primaryButtonClass } from "../lightTheme"
import { heroContent } from "./heroContent"

const cardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const chipShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const boldStats = ["[0.12%]", "[$52,400/mo]"]

function LogLine({ line }) {
  const stat = boldStats.find((item) => line.startsWith(item))
  if (!stat) return line
  return (
    <>
      <span className="font-semibold">{stat}</span>
      {line.slice(stat.length)}
    </>
  )
}

export default function HeroSection() {
  const [tabId, setTabId] = useState(heroContent.tabs[0].id)
  const tab = heroContent.tabs.find((item) => item.id === tabId) ?? heroContent.tabs[0]

  return (
    <div className="flex w-full translate-x-[calc(50vw-50%-1.5rem)] flex-col gap-24">
      <section className="w-full pt-16 md:pt-24 lg:pt-36">
        <div className="grid w-full grid-cols-1 items-center gap-x-6 gap-y-12 lg:grid-cols-12">
          <div className="flex flex-col gap-8 lg:col-span-6">
            <div className="flex flex-col gap-3">
              <p className="text-[14px] font-medium leading-5 text-[#4D4D4D]">
                {heroContent.kicker}
              </p>
              <h1 className="text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8">
                {heroContent.h1}
              </h1>
            </div>
            <p className="max-w-[64ch] text-[18px] font-normal leading-7 text-[#4D4D4D]">
              {heroContent.lead}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button type="button" className={`${primaryButtonClass} gap-1`}>
                {heroContent.cta}
                <span aria-hidden="true">↗</span>
              </Button>
              <div
                className={`inline-flex h-10 items-center gap-1 rounded-full bg-white py-1.5 pl-5 pr-2 ${chipShadow}`}
              >
                <span className="font-mono text-[14px] leading-6 text-[#171717]">
                  {heroContent.chip}
                </span>
                <button
                  type="button"
                  aria-label="Search"
                  className={`inline-flex size-7 cursor-pointer items-center justify-center rounded-full text-[#A8A8A8] ${focusRing}`}
                >
                  <Search className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <div className={`flex w-full flex-col overflow-hidden rounded-xl bg-white ${cardShadow}`}>
              <div className="flex h-10 bg-[#FAFAFA]" role="tablist">
                {heroContent.tabs.map((item) => {
                  const selected = item.id === tab.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setTabId(item.id)}
                      className={`h-full cursor-pointer whitespace-nowrap px-3 text-[13px] font-normal leading-5 ${focusRing} ${
                        selected ? "bg-white text-[#171717]" : "bg-transparent text-[#4D4D4D]"
                      }`}
                    >
                      {item.label}
                    </button>
                  )
                })}
              </div>
              {tab.search ? (
                <form
                  className="flex min-h-[220px] flex-col items-start justify-center gap-3 bg-white p-5"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <label className="flex w-full flex-col gap-2" htmlFor="company-search">
                    <span className="text-[13px] font-light leading-[18px] text-[#171717]">
                      {tab.search.label}
                    </span>
                    <input
                      id="company-search"
                      type="search"
                      placeholder={tab.search.placeholder}
                      className={`${fieldInputClass} w-full`}
                    />
                  </label>
                  <button type="submit" className={`${formButtonClass} gap-1.5`}>
                    <Search className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
                    {tab.search.button}
                  </button>
                </form>
              ) : (
                <div className="min-h-[220px] bg-white py-4 font-mono text-[13px] leading-5">
                  {tab.lines.map((line, index) => (
                    <div key={`${tab.id}-${index}`} className={`flex ${line ? "" : "h-5"}`}>
                      <span className="w-8 shrink-0 select-none text-right text-[#A8A8A8]">
                        {index + 1}
                      </span>
                      <span className="min-w-0 whitespace-pre-wrap pl-4 text-[#171717]">
                        <LogLine line={line} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="mt-4 text-right text-[12px] font-medium leading-4 text-[#666666]">
              {heroContent.caption}
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
